import {
	Alert,
	Box,
	Button,
	CircularProgress,
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Snackbar,
	Step,
	StepContent,
	StepLabel,
	Stepper,
	TextField,
	Typography,
} from "@mui/material"
import { AxiosError, AxiosResponse } from "axios"
import { ChangeEvent, useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"

import { ReactComponent as CompletingSvg } from "../../../assets/img/completing.svg"
import { AppDispatch, RootState } from "../../../store"
import { BankEssential } from "../../../store/actions/bank"
import {
	createUser,
	getAllowedDomain,
	getRolePermissions,
	Permission,
	PermissionState,
	updateUser,
	UserData,
	UserState,
} from "../../../store/actions/user"
import { selectBank } from "../../../store/bank/bank-all-slice"
import { toRefreshList } from "../../../store/user/user-edit-slice"
import { supportedLanguage } from "../../../utils/supported-language"
import BankAutoComplete from "../../filters/bank-autocomplete"
import CorporateAutoComplete from "../../filters/corporate-autocomplete"
import RoleAutoComplete from "../../filters/role-autocomplete"
import EditPermissions from "./edit-permissions"
import EmailAutosuggest from "./email-autosuggest"

interface ICreateFormProps {
	isEditMode?: boolean
	user?: UserState | undefined
	onClose: () => void
}

const CreateForm = ({ isEditMode, user }: ICreateFormProps) => {
	const { t } = useTranslation()
	const dispatch = useDispatch<AppDispatch>()
	const banksList = useSelector((state: RootState) => state.banksUser?.list)
	const [alertMessage, setAlertMessage] = useState<string | undefined>(
		undefined
	)
	const [allowedDomain, setAllowedDomain] = useState<string[] | undefined>(
		undefined
	)
	const [activeStep, setActiveStep] = useState<number>(0)
	const [isErrorSnackBarOpen, setIsErrorSnackBarOpen] = useState<boolean>(false)
	const [isSavingForm, setIsSavingForm] = useState<boolean>(false)
	const [errors, setErrors] = useState(undefined)
	const [formData, setFormData] = useState<UserData>({
		idBank: undefined,
		corporateSenderId: undefined,
		fullName: "",
		description: "",
		enabled: true,
		username: "",
		phone: undefined,
		email: "",
		reset: true,
		role: "",
		language: "it",
		bankName: "",
		corporateName: "",
		reportSection: true,
		distinteSection: true,
		permissions: undefined,
	})
	const [updateFormData, setUpdateFormData] = useState<UserData | undefined>(
		undefined
	)
	const steps = isEditMode
		? [
				t("form.steps.permissions", { ns: "user" }),
				t("form.steps.userData", { ns: "user" }),
			]
		: [
				t("form.steps.role", { ns: "user" }),
				t("form.steps.permissions", { ns: "user" }),
				t("form.steps.bank", { ns: "user" }),
				t("form.steps.corporate", { ns: "user" }),
				t("form.steps.userData", { ns: "user" }),
			]
	const arrayNoEntities = new Set([
		"PAYDO_ADMIN",
		"PAYDO_MANAGER",
		"PAYDO_USER",
	])
	const arrayWithCorporate = new Set([
		"CORPORATE_ADMIN",
		"CORPORATE_OPERATOR",
		"BANK_CORPORATE_ADMIN",
		"BANK_CORPORATE_AUTHORIZER",
		"CORPORATE_AUTHORIZER",
	])

	useEffect(() => {
		if (isEditMode) {
			setFormData({
				idBank: user?.idBank,
				corporateSenderId: user?.corporateSenderId,
				fullName: user?.fullName || "",
				description: user?.description || "",
				enabled: user?.enabled || true,
				username: user?.username || "",
				phone: user?.phone,
				email: user?.email || "",
				reset: user?.reset || true,
				role: user?.roles?.[0] || "",
				language: user?.language || "it",
				bankName: user?.bankName || "",
				corporateName: user?.corporateName || "",
				reportSection: user?.reportSection || true,
				distinteSection: user?.distinteSection || true,
				permissions: user?.permissions || undefined,
			})
		}
	}, [user])

	useEffect(() => {
		if (!formData?.role) return
		if (!isEditMode) {
			dispatch(getRolePermissions({ role: formData?.role }))
				.then((response) => {
					const permissionRes: PermissionState = response?.payload.map(
						(el: Permission[]) => ({
							permissionName: el?.permission?.name,
							enabled: el.enabled,
							editable: el.editable,
						})
					)
					setFormData((prevData) => ({
						...prevData,
						permissions: permissionRes,
					}))
				})
				.catch((e) => {})
		}
	}, [formData?.role])

	useEffect(() => {
		if (!formData?.idBank) return
		const selectedBank: BankEssential | undefined = banksList.find(
			(el: BankEssential) => el.id === formData.idBank
		)
		dispatch(selectBank(selectedBank))

		if (
			!isEditMode &&
			!arrayNoEntities.has(formData?.role) &&
			!arrayWithCorporate.has(formData?.role)
		)
			dispatch(
				getAllowedDomain({
					organizationType: "bank",
					organizationId: formData?.idBank,
				})
			)
				.then((response) => {
					setAllowedDomain(response?.payload)
				})
				.catch((e) => {})
	}, [formData?.role, formData?.idBank])

	useEffect(() => {
		if (!formData?.corporateSenderId) return

		if (arrayWithCorporate.has(formData?.role))
			dispatch(
				getAllowedDomain({
					organizationType: "corporate",
					organizationId: formData?.corporateSenderId,
				})
			)
				.then((response) => {
					setAllowedDomain(response?.payload)
				})
				.catch((e) => {})
	}, [formData?.role, formData?.corporateSenderId])

	const handleNext = useCallback(() => {
		if (activeStep === steps.length - 1) {
			if (isEditMode) {
				dispatch(updateUser({ userData: updateFormData, idUser: user?.id }))
					.then((result: AxiosResponse) => {
						if (result?.meta?.requestStatus === "rejected") {
							if (result?.payload?.status === 404) {
								setAlertMessage(
									t("unauthorized.error.alertMessage", { ns: "common" })
								)
								setIsErrorSnackBarOpen(true)
							} else if (result?.payload?.status === 400) {
								const backendErrors = result?.payload.data.reduce(
									(acc, err) => {
										acc[err.field] = err.defaultMessage
										return acc
									},
									{}
								)
								setErrors(backendErrors)
							}
						} else {
							setActiveStep((prevActiveStep) => prevActiveStep + 1)
						}
					})
					.catch((error: AxiosError) => {
						if (error.response && error.response.status === 400) {
							const backendErrors = error.response.data.reduce((acc, err) => {
								acc[err.field] = err.defaultMessage
								return acc
							}, {})
							setErrors(backendErrors)
						} else if (error.response && error.response.status === 404) {
							setAlertMessage(
								t("unauthorized.error.alertMessage", { ns: "common" })
							)
							setIsErrorSnackBarOpen(true)
						}
					})
			} else {
				setIsSavingForm(true)
				dispatch(createUser({ userData: formData }))
					.then((result: AxiosResponse) => {
						setIsSavingForm(false)
						if (result?.meta?.requestStatus === "rejected") {
							if (result?.payload?.status === 404) {
								setAlertMessage(
									t("unauthorized.error.alertMessage", { ns: "common" })
								)
								setIsErrorSnackBarOpen(true)
							} else if (result?.payload?.status === 400) {
								const backendErrors = result?.payload.data.reduce(
									(acc, err) => {
										acc[err.field] = err.defaultMessage
										return acc
									},
									{}
								)
								setErrors(backendErrors)
							}
						} else {
							dispatch(toRefreshList())
							setActiveStep((prevActiveStep) => prevActiveStep + 1)
						}
					})
					.catch((error: AxiosError) => {
						setIsSavingForm(false)
						if (error.response && error.response.status === 400) {
							const backendErrors = error.response.data.reduce((acc, err) => {
								acc[err.field] = err.defaultMessage
								return acc
							}, {})
							setErrors(backendErrors)
						} else if (error.response && error.response.status === 404) {
							setAlertMessage(
								t("unauthorized.error.alertMessage", { ns: "common" })
							)
							setIsErrorSnackBarOpen(true)
						}
					})
			}
		} else if (
			(!isEditMode &&
				activeStep === 2 &&
				!arrayWithCorporate.has(formData?.role)) ||
			(activeStep === 1 && arrayNoEntities.has(formData?.role))
		) {
			setActiveStep(4)
		} else {
			setActiveStep((prevActiveStep) => prevActiveStep + 1)
		}
	}, [activeStep, formData])

	const handleBack = () => {
		if (
			!isEditMode &&
			activeStep === 4 &&
			arrayNoEntities.has(formData?.role)
		) {
			setActiveStep(1)
		} else if (
			!isEditMode &&
			activeStep === 4 &&
			!arrayWithCorporate.has(formData?.role)
		) {
			setActiveStep(2)
		} else {
			setActiveStep((prevActiveStep) => prevActiveStep - 1)
		}
	}

	const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { name, value } = event.target

		if (isEditMode && formData?.[name] !== value) {
			setUpdateFormData((prevData) => ({
				...prevData,
				[name]: value,
			}))
		}

		setFormData((prevData) => ({
			...prevData,
			[name]: value,
		}))
	}

	const isDisabled = useCallback(
		(stepIndex: number) => {
			if (isEditMode) {
				switch (stepIndex) {
					case 0: {
						return !formData?.permissions
					}
					case 1: {
						return (
							!formData?.username || !formData?.fullName || !formData?.email
						)
					}
					default: {
						return true
					}
				}
			} else {
				switch (stepIndex) {
					case 0: {
						return !formData?.role
					}
					case 1: {
						return !formData?.permissions
					}
					case 2: {
						return !formData?.idBank
					}
					case 3: {
						return !formData?.corporateSenderId
					}
					case 4: {
						return (
							!formData?.username || !formData?.fullName || !formData?.email
						)
					}
					default: {
						return true
					}
				}
			}
		},
		[formData]
	)

	const changePermissions = useCallback(
		(indexArray: number) => {
			let objectToUpdate
			objectToUpdate = {
				permissions: updateFormData?.permissions || formData?.permissions,
			}

			const patchPermissions = [...objectToUpdate?.permissions]
			patchPermissions[indexArray] = {
				...patchPermissions[indexArray],
				enabled: !patchPermissions[indexArray].enabled,
			}
			setUpdateFormData((prevData: UserData) => ({
				...prevData,
				permissions: patchPermissions,
			}))
		},
		[updateFormData, formData]
	)

	const userForm = () => {
		return (
			<Box
				component="form"
				onSubmit={() => {}}
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: 3,
					maxWidth: 400,
					margin: "16px auto",
				}}
			>
				<TextField
					fullWidth
					InputLabelProps={{ shrink: true }}
					error={!!errors?.fullName}
					helperText={t(errors?.fullName, { ns: "user" })}
					id="fullName"
					label={t("form.fullname", { ns: "user" })}
					name="fullName"
					value={formData?.fullName}
					onChange={handleInputChange}
					required
				/>
				<EmailAutosuggest
					error={!!errors?.email}
					helperText={t(errors?.email, { ns: "user" })}
					disabled={isEditMode}
					allowedDomains={allowedDomain}
					email={formData?.email}
					handleInputChange={handleInputChange}
				/>
				<TextField
					fullWidth
					InputLabelProps={{ shrink: true }}
					error={!!errors?.username}
					helperText={t(errors?.username, { ns: "user" })}
					id="username"
					label={t("form.username", { ns: "user" })}
					name="username"
					disabled={isEditMode}
					value={formData?.username}
					onChange={handleInputChange}
					required
				/>
				<TextField
					fullWidth
					InputLabelProps={{ shrink: true }}
					error={!!errors?.description}
					helperText={t(errors?.description, { ns: "user" })}
					id="description"
					label={t("form.description", { ns: "user" })}
					name="description"
					value={formData?.description}
					onChange={handleInputChange}
				/>

				<TextField
					InputLabelProps={{ shrink: true }}
					fullWidth
					id="phone"
					label={t("form.phone", { ns: "user" })}
					name="phone"
					value={formData?.phone}
					onChange={handleInputChange}
				/>
				<FormControl fullWidth>
					<InputLabel id="language-selector-label">
						{t("form.language", { ns: "user" })}
					</InputLabel>
					<Select
						labelId="language-selector-label"
						id="language-selector"
						value={formData?.language}
						label={t("form.language", { ns: "user" })}
						autoWidth={true}
					>
						{supportedLanguage.map((lang: { value: string; label: string }) => {
							return (
								<MenuItem
									key={`lang_${lang.value}`}
									onClick={() => {
										setFormData((prevData) => ({
											...prevData,
											language: lang.value,
										}))
									}}
									value={lang.value}
								>
									{lang.label.toUpperCase()}
								</MenuItem>
							)
						})}
					</Select>
				</FormControl>
			</Box>
		)
	}

	const getStepContent = (stepIndex: number) => {
		if (isEditMode) {
			switch (stepIndex) {
				case 0: {
					return (
						<EditPermissions
							changePermissions={changePermissions}
							permissions={formData?.permissions}
						/>
					)
				}
				case 1: {
					return userForm()
				}
				default: {
					return undefined
				}
			}
		} else {
			switch (stepIndex) {
				case 0: {
					return (
						<RoleAutoComplete
							disableClearable={false}
							filterRole={formData?.role}
							setFilterRole={(value: string | undefined) =>
								setFormData((prevData: UserData) => ({
									...prevData,
									role: value,
								}))
							}
						/>
					)
				}
				case 1: {
					return (
						<EditPermissions
							changePermissions={changePermissions}
							permissions={formData?.permissions}
						/>
					)
				}
				case 2: {
					return (
						<BankAutoComplete
							disableClearable={false}
							filterBank={formData?.idBank}
							setFilterBank={(value: string | undefined) =>
								setFormData((prevData: UserData) => ({
									...prevData,
									idBank: value,
								}))
							}
							setFilterBankName={(value: string | undefined) =>
								setFormData((prevData: UserData) => ({
									...prevData,
									bankName: value,
								}))
							}
						/>
					)
				}
				case 3: {
					return (
						<CorporateAutoComplete
							filterCorporate={formData?.corporateSenderId}
							setFilterCorporate={(value: string | undefined) =>
								setFormData((prevData: UserData) => ({
									...prevData,
									corporateSenderId: value,
								}))
							}
						/>
					)
				}
				case 4: {
					return userForm()
				}
				default: {
					return undefined
				}
			}
		}
	}

	const getStepSummary = (stepIndex: number) => {
		if (!isEditMode) {
			switch (stepIndex) {
				case 0: {
					return `${formData?.role ? ": " + formData.role : ""}`
				}
				case 2: {
					return `${formData?.bankName ? ": " + formData.bankName : ""}`
				}
				case 3: {
					return `${formData?.corporateName ? ": " + formData.corporateName : ""}`
				}
				default: {
					return ""
				}
			}
		}
	}

	return (
		<>
			{activeStep < steps.length && (
				<Stepper activeStep={activeStep} orientation="vertical">
					{steps.map((step, index) => {
						return (
							<Step key={`step_${index}`}>
								<StepLabel
									style={{
										textDecoration:
											formData?.role &&
											((index === 3 &&
												!arrayWithCorporate.has(formData?.role)) ||
												((index === 2 || index === 3) &&
													arrayNoEntities.has(formData?.role)))
												? "line-through"
												: "none",
										textDecorationThickness:
											formData?.role &&
											((index === 3 &&
												!arrayWithCorporate.has(formData?.role)) ||
												((index === 2 || index === 3) &&
													arrayNoEntities.has(formData?.role)))
												? "3px"
												: 0,
									}}
									optional={
										index === steps.length - 1 ? (
											<Typography component="div" sx={{}} variant="caption">
												{t("form.lastStep", { ns: "user" })}
											</Typography>
										) : null
									}
								>
									{step} <b>{getStepSummary(index)}</b>
								</StepLabel>
								<StepContent>
									<Box sx={{ mb: 3 }}>
										{getStepContent(activeStep)}
										<Box
											sx={{
												display: "flex",
												justifyContent: "space-beetween",
												mt: 3,
												zIndex: 9,
											}}
										>
											{isSavingForm ? (
												<CircularProgress size="50px" />
											) : (
												<Button
													disabled={isDisabled(activeStep)}
													variant="contained"
													onClick={handleNext}
													sx={{ margin: "16px 0" }}
												>
													{index === steps.length - 1
														? t("form.buttons.save", { ns: "user" })
														: t("form.buttons.continue", { ns: "user" })}
												</Button>
											)}
											{index > 0 && (
												<Button onClick={handleBack} sx={{ mt: 2, mr: 2 }}>
													{t("form.buttons.back", { ns: "user" })}
												</Button>
											)}
										</Box>
									</Box>
								</StepContent>
							</Step>
						)
					})}
				</Stepper>
			)}
			{activeStep >= steps.length && (
				<Paper square elevation={0} sx={{ p: 4 }}>
					<Grid container>
						<Grid item>
							<CompletingSvg width={"200px"} height={"200px"} />
						</Grid>
						<Grid item sx={{ alignContent: "center" }}>
							<Typography sx={{ fontWeight: "bold" }} component="div">
								{t(isEditMode ? "form.modified" : "form.created", {
									ns: "user",
									fullname: formData?.fullName,
								})}
							</Typography>
						</Grid>
					</Grid>
				</Paper>
			)}
			{!!alertMessage && (
				<Snackbar
					sx={{ width: "calc(200% - 50px)" }}
					anchorOrigin={{ vertical: "top", horizontal: "left" }}
					open={isErrorSnackBarOpen}
					autoHideDuration={30000}
					onClose={() => setIsErrorSnackBarOpen(false)}
				>
					<Alert
						onClose={() => setIsErrorSnackBarOpen(false)}
						severity="error"
						variant="filled"
						sx={{ width: "200%" }}
					>
						{alertMessage}
					</Alert>
				</Snackbar>
			)}
		</>
	)
}

export default CreateForm
